/* Flight Foods — PDP gallery fill (Oct 30 2026, v3)
   The main product image is shown whole. When its shape doesn't match the frame, each image edge that
   borders empty space is read first:
     - perfectly flat edge (black facts panel, yellow graphic)  -> the edge is extended outward, seamless
     - mostly one colour with some detail                       -> filled with that solid colour (no streaks)
     - a real photo on every gap side (lifestyle shots)         -> the photo is cropped to fill the frame
   Graphics with a flat side (facts, how-to, key benefits) are never cropped.
   Pairs with assets/ff-pdp-gallery.css. */
(() => {
  const PAD = 16, RES = 0.5;
  const TOL = 22;          // colour distance that still counts as "the same colour"
  const FLAT = 0.97;       // share of an edge that must match for a seamless stretch
  const MOSTLY = 0.80;     // share of an edge that must match to count as a plain background
  const MAX_CROP = 0.40;   // never crop a photo by more than this
  const cache = new Map();

  const analyse = src => {
    if (cache.has(src)) return cache.get(src);
    const p = new Promise(res => {
      const a = new Image();
      a.crossOrigin = 'anonymous';
      a.onload = () => {
        try {
          const s = Math.min(1, 240 / Math.max(a.naturalWidth, a.naturalHeight));
          const w = Math.max(4, Math.round(a.naturalWidth * s)), h = Math.max(4, Math.round(a.naturalHeight * s));
          const c = document.createElement('canvas'); c.width = w; c.height = h;
          const x = c.getContext('2d', { willReadFrequently: true }); x.drawImage(a, 0, 0, w, h);
          const d = x.getImageData(0, 0, w, h).data;
          const px = (i, j) => { const o = (j * w + i) * 4; return [d[o], d[o + 1], d[o + 2]]; };
          const edge = list => {
            const med = [0, 1, 2].map(k => { const v = list.map(p => p[k]).sort((m, n) => m - n); return v[v.length >> 1]; });
            const ok = list.filter(p => Math.max(Math.abs(p[0] - med[0]), Math.abs(p[1] - med[1]), Math.abs(p[2] - med[2])) <= TOL).length;
            return { med, frac: ok / list.length };
          };
          const col = i => { const l = []; for (let j = 0; j < h; j++) { l.push(px(i, j)); l.push(px(i + (i ? -1 : 1), j)); } return l; };
          const row = j => { const l = []; for (let i = 0; i < w; i++) { l.push(px(i, j)); l.push(px(i, j + (j ? -1 : 1))); } return l; };
          res({ left: edge(col(1)), right: edge(col(w - 2)), top: edge(row(1)), bottom: edge(row(h - 2)) });
        } catch (e) { res(null); } // image not readable (CORS) -> simple stretch fallback
      };
      a.onerror = () => res(null);
      a.src = src;
    });
    cache.set(src, p);
    return p;
  };

  const init = frame => {
    if (!frame || frame.dataset.ffFill) return;
    frame.dataset.ffFill = '1';
    const cv = document.createElement('canvas');
    cv.className = 'gallery__fill'; cv.setAttribute('aria-hidden', 'true');
    frame.prepend(cv);
    const ctx = cv.getContext('2d');
    const img = () => frame.querySelector(':scope > img:not(.gallery__blur)');
    const off = () => cv.classList.remove('is-on');
    const setCover = (im, on) => { if (on) im.style.setProperty('object-fit', 'cover', 'important'); else im.style.removeProperty('object-fit'); frame.classList.toggle('is-cropped', on); };
    let token = 0;

    const paint = async () => {
      const t = ++token;
      const im = img();
      if (!im || !ctx) return off();
      if (im.classList.contains('contain')) { setCover(im, false); return off(); }
      if (!im.complete || !im.naturalWidth) return;
      const src = im.currentSrc || im.src;
      const st = await analyse(src);
      if (t !== token || (im.currentSrc || im.src) !== src) return; // a newer image took over
      const W = frame.clientWidth, H = frame.clientHeight, iw = im.naturalWidth, ih = im.naturalHeight;
      if (!W || !H) return;
      const s = Math.min(W / iw, H / ih), dw = iw * s, dh = ih * s, dx = (W - dw) / 2, dy = (H - dh) / 2;
      if (dx < 1 && dy < 1) { setCover(im, false); return off(); }
      const sides = dx >= 1 ? ['left', 'right'] : ['top', 'bottom'];
      const crop = dx >= 1 ? 1 - dw / W : 1 - dh / H;

      // Lifestyle photo (every gap side is busy photo content): crop to fill, no fill needed
      const cutSides = dx >= 1 ? ['top', 'bottom'] : ['left', 'right']; // edges a crop would trim away
      const isPhoto = st && sides.every(k => st[k].frac < MOSTLY) && !cutSides.every(k => st[k].frac >= FLAT);
      if (isPhoto && crop <= MAX_CROP) { setCover(im, true); return off(); }
      setCover(im, false);

      const CW = W + PAD * 2, CH = H + PAD * 2;
      cv.style.left = cv.style.top = -PAD + 'px';
      cv.style.width = CW + 'px'; cv.style.height = CH + 'px';
      cv.width = Math.max(1, Math.round(CW * RES)); cv.height = Math.max(1, Math.round(CH * RES));
      ctx.setTransform(RES, 0, 0, RES, PAD * RES, PAD * RES);
      ctx.clearRect(-PAD, -PAD, CW, CH);
      ctx.imageSmoothingEnabled = true;
      const k = Math.max(1, Math.round(Math.min(iw, ih) * 0.004)), i = 1, R = dx + dw, B = dy + dh;
      // region of the frame (incl. overshoot) that each side has to cover
      const area = { left: [-PAD, -PAD, dx + PAD + 1, CH], right: [R - 1, -PAD, W + PAD - R + 1, CH], top: [-PAD, -PAD, CW, dy + PAD + 1], bottom: [-PAD, B - 1, CW, H + PAD - B + 1] };
      const stretch = {
        left: () => ctx.drawImage(im, i, 0, k, ih, -PAD, dy, dx + PAD + 1, dh),
        right: () => ctx.drawImage(im, iw - i - k, 0, k, ih, R - 1, dy, W + PAD - R + 1, dh),
        top: () => ctx.drawImage(im, 0, i, iw, k, dx, -PAD, dw, dy + PAD + 1),
        bottom: () => ctx.drawImage(im, 0, ih - i - k, iw, k, dx, B - 1, dw, H + PAD - B + 1)
      };
      try {
        sides.forEach(side => {
          const e = st && st[side];
          if (e) { ctx.fillStyle = `rgb(${e.med[0]},${e.med[1]},${e.med[2]})`; ctx.fillRect(...area[side]); } // solid base (also fills overshoot corners)
          if (!e || e.frac >= FLAT) stretch[side]();                                                        // flat edge: extend it exactly
        });
        ctx.drawImage(im, dx, dy, dw, dh); // the image underneath so the soft edge blends into it
        cv.classList.add('is-on');
      } catch (e) { off(); }
    };

    let raf = 0;
    const queue = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(paint); };
    const watchImg = () => { const im = img(); if (im && !im.dataset.ffWatch) { im.dataset.ffWatch = '1'; im.addEventListener('load', queue); } };
    watchImg(); queue();
    if ('ResizeObserver' in window) new ResizeObserver(queue).observe(frame);
    new MutationObserver(muts => {
      if (muts.some(m => m.attributeName === 'src')) { off(); const im = img(); if (im) setCover(im, false); }
      watchImg(); queue();
    }).observe(frame, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset', 'class'] });
    addEventListener('load', queue);
  };

  const run = root => (root || document).querySelectorAll('.gallery__main').forEach(init);
  run();
  document.addEventListener('shopify:section:load', e => run(e.target));

  // keep the selected thumbnail visible inside the scrolling rail
  document.addEventListener('click', e => {
    const t = e.target.closest('.gallery__thumbs button'); if (!t) return;
    const rail = t.parentElement, r = t.getBoundingClientRect(), rr = rail.getBoundingClientRect();
    if (r.top < rr.top || r.bottom > rr.bottom) rail.scrollTop += (r.top - rr.top) - (rr.height - r.height) / 2;
    if (r.left < rr.left || r.right > rr.right) rail.scrollLeft += (r.left - rr.left) - (rr.width - r.width) / 2;
  });
})();
